// ---------------------------------------------------------------------------
// chunkReload — Erholung nach Deploy fuer lazy-geladene Bausteine (POL-1).
// ---------------------------------------------------------------------------
// Problem: Die App laedt Teile lazy (dynamic import mit gehashtem Dateinamen,
// z.B. DashboardCEO, PushPrefsDialog). Nach einem neuen Deploy existiert der
// alte Hash nicht mehr. Ein Nutzer, der die App ueber einen Deploy hinweg
// offen laesst und dann einen lazy-Baustein nachlaedt, bekommt sonst einen
// stillen "Failed to fetch dynamically imported module"-Fehler — der Klick
// laeuft ins Leere, keine Meldung, keine Erholung.
//
// Loesung: Genau diesen Fehler erkennen und EINMALIG kontrolliert neu laden
// (Guard gegen Endlos-Schleife via sessionStorage). Schlaegt der Reload sofort
// wieder fehl (echtes Netzproblem), zeigt die UI stattdessen einen manuellen
// "Neu laden"-Hinweis. Normale Render-Fehler sind NICHT betroffen — die laufen
// weiter an die ErrorBoundary + Sentry.

// sessionStorage-Schluessel + Zeitfenster fuer den Schleifen-Guard. Kommt nach
// unserem eigenen Reload INNERHALB dieses Fensters sofort wieder ein
// Chunk-Fehler, war Reload nicht die Loesung → nicht in die Schleife gehen.
const RELOAD_FLAG = "bergler.chunk-reload-at";
const RELOAD_LOOP_WINDOW_MS = 10_000;

// Verschiedene Browser + Bundler formulieren den Fehler unterschiedlich.
const CHUNK_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i, // Chromium / Vite
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
  /unable to preload css/i, // Vite CSS-Preload-Helper
  /loading chunk [\d]+ failed/i, // webpack-Stil
  /dynamically imported module/i, // Auffangnetz
];

/**
 * Erkennt einen Chunk-/Dynamic-Import-Ladefehler (stale Deploy), abgegrenzt
 * von echten Render-Fehlern. Akzeptiert Error, String oder unknown (Rejection-
 * reason kann alles sein).
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  if (
    typeof error === "object" &&
    "name" in error &&
    (error as { name?: unknown }).name === "ChunkLoadError"
  ) {
    return true;
  }
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

/**
 * Entscheidet, ob JETZT ein kontrollierter Reload ausgeloest werden darf.
 * Setzt beim OK den Zeitstempel-Flag; blockt, wenn der letzte eigene Reload
 * < 10s zurueckliegt (Schleifen-Schutz). Pure genug fuer Unit-Tests.
 */
export function decideChunkReload(): boolean {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_FLAG);
    if (raw) {
      const at = Number(raw);
      if (Number.isFinite(at) && Date.now() - at < RELOAD_LOOP_WINDOW_MS) {
        return false;
      }
    }
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
    return true;
  } catch {
    // sessionStorage unerreichbar (Privacy-Mode) — ohne Guard kein Auto-Reload.
    return false;
  }
}

/**
 * Loest einen einmaligen, geguardeten Reload aus. Rueckgabe: true, wenn der
 * Reload tatsaechlich angestossen wurde (dann navigiert die Seite gleich weg);
 * false, wenn der Schleifen-Guard griff (dann bleibt der manuelle Hinweis).
 */
export function requestChunkReload(): boolean {
  if (decideChunkReload()) {
    window.location.reload();
    return true;
  }
  return false;
}

let listenerInstalled = false;

/**
 * Fensterweite Auffangnetze fuer dynamic-import-Fehler ausserhalb des React-
 * Render-Pfads (z.B. import() in Event-Handlern) plus Vites offizielles
 * `vite:preloadError`-Signal. Nur Chunk-Fehler werden behandelt — alles andere
 * laeuft unveraendert weiter an Sentry / die ErrorBoundary.
 */
export function installChunkErrorListener(): void {
  if (typeof window === "undefined" || listenerInstalled) return;
  listenerInstalled = true;

  // Vites eingebautes Signal, wenn der Preload eines dynamic-import-Chunks
  // fehlschlaegt. Greift der Reload → preventDefault (Vite wirft dann nicht
  // mehr). Wird der Reload geguarded → NICHT preventDefault, damit Vite wirft
  // und die ChunkErrorBoundary den manuellen Hinweis zeigt.
  window.addEventListener("vite:preloadError", (event: Event) => {
    if (requestChunkReload()) {
      event.preventDefault();
    }
  });

  // import() in Event-Handlern (nicht ueber React.lazy/Suspense) landet als
  // unhandledrejection. Nur Chunk-Fehler behandeln, Rest durchlassen.
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      requestChunkReload();
    }
  });
}
